import { useEffect, useRef, useState } from "react"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { useInterval } from "../hooks/useInterval"
import { pollingIntervals } from "../constants/pollingIntervals"
import { defaultDeliveryMethodOptions } from "../Components/FormFields/DeliveryMethodSelectList"
import { assert } from "@cd2/tools/build/es6"
import { differenceInSeconds } from "date-fns/esm"
import { fortusEwBranchId } from "../../shared/constants"

export type IBranchSettings = typeof defaultSettings

/** A write off category that can be picked when a stock take write off is exported. */
export interface IWriteOffCategorySetting {
  key: string
  /** The category name, as it is known in Sage */
  value: string
  /** The nominal code the write off is posted against */
  nominal: string
}

type IAddress = {
  id: string
  name: string
  line_1: string
  line_2: string
  line_3: string
  line_4: string
  city: string
  county: string
  country: string
  postcode: string
  phone: string
  email: string
}

export const defaultSettings = {
  defaultStockLocation: "",

  pdfAddress: "",
  companyRegNumber: "",
  vatNumber: "",
  pdfDiscrepancyTime: "",
  pdfReturnsTime: "",
  statementBankAccountNumber: "",
  statementBankSortCode: "",
  statementBankAccountNumberCashSale: "",
  statementBankSortCodeCashSale: "",
  pdfIBAN: "",
  pdfSWIFT: "",
  pdfIBANCashSales: "",
  pdfSWIFTCashSales: "",

  dpdService: "none" as "none" | "dpd" | "dpd-local" | "dpd-ireland",
  dpdIrelandUrl: "",
  dpdUsername: "",
  dpdProductPrefixes: "",
  dpdPassword: "",
  dpdAccountNumber: "",
  dpdCountryCode: "",
  dpdPostcode: "",
  dpdOrganisation: "",
  dpdStreet: "",
  dpdLocality: "",
  dpdTown: "",
  dpdCounty: "",
  dpdContactName: "",
  dpdTelephone: "",
  dpdNotificationEmails: [] as { email: string; name: string }[],
  fedexAccountNumber: "",
  enableDelete: false,
  enableOnHoldOrders: false,
  barScanOrders: false,
  barscanWarehouseTransferV2: false,
  warehouses: [] as { id: string; name: string }[],
  stockWarehouses: [] as { id: string; name: string }[],
  orderRequestIdentifierPrefix: "",
  branchPoAddress: null as IAddress | null,
  orderRequestIndexView: "outgoing" as "outgoing" | "incoming",
  quoteFooterText: "",
  company: "esd",
  showDiscountOption: false,
  showOriginalPriceWhenShowingDiscountOption: false,
  useAverageBuyingPrice: false,
  notificationEmailsCustomerJobCreated: [] as string[],
  notificationEmailsCustomerJobComment: [] as string[],
  notificationEmailsCustomerQuoteAccepted: [] as string[],
  notificationEmailsOrderRequestAssignedToOrder: [] as string[],
  notificationEmailsOrderRequestCreated: [] as string[],
  notificationEmailsProductGroupMargins: [] as string[],
  notificationEmailsNewCustomerAccount: [] as string[],
  notificationEmailsNewTradeApplication: [] as string[],
  notificationEmailsNewReturnRequest: [] as string[],
  notificationEmailsActionedTradeApplications: [] as string[],
  notificationEmailsHolidayRequests: [] as string[],
  notificationEmailsAlertStockTake: [] as string[],
  notificationEmailsProbationReview: [] as string[],
  notificationEmailsDailyBranchSalesReport: [] as string[],
  notificationEmailsDailyOrderRequestsReport: [] as string[],
  notificationEmailsDailyCashSalesReport: [] as string[],
  // sites a payment can be taken at (e.g. Dublin, Cork). When set, one must be picked with a payment
  paymentLocations: [] as string[],
  holidayRequestReportEmails: [] as string[],
  dailyBranchStockReport: [] as string[],
  noValidDateOnQuotes: false,

  branchDirectorUsers: [] as { id: string; name: string; skeem_email: string }[],

  // customer area my branch things
  branchName: "",
  branchEmail: "",
  branchPhone: "",
  branchWebsite: "",
  branchAddress: "",
  branchPostcode: "",
  branchEmails: [] as { email: string; name: string }[],

  barscanUrl: "",

  displayQuotesWithSellingPriceAndDiscounts: false,
  defaultCountry: "United Kingdom",

  dpdLabelFormat: "html",
  dpdPrinterPath: "",
  fedexPrinterPath: "",
  a4PrinterPath: "",
  showBillingAddressOnOrders: false as boolean,
  alwaysShowBillingForCashSalesOnOrders: false as boolean,
  minimumLineItemMargin: null as number | null,
  salesRepAnalysisCode: "",
  analysisCodeSoId: "",
  sageCarriageItemProductCode: "" as string,
  deliveryOptions: defaultDeliveryMethodOptions,
  sendMonthlyPriceLists: false,
  alwaysPresentChooseDeliveryAddress: false,
  sageDefaultCurrency: "GBP",
  EOSDefaultCurrency: "GBP",
  exportEosIdentifierPrefix: "" as string,
  allowZeroPriceQuotesOrders: false as boolean,
  // True so a page that has not loaded (or failed to load) settings caps allocations to
  // available stock rather than skipping the check. Loaded settings use the branch's stored value.
  useExplicitAllocations: true as boolean,
  displayOrderQtyOnPickingTicketWhenUsingExplicitAlloc: false as boolean,
  showPriceOnPickingTicket: true as boolean,
  excludeFromGroupedPricing: false as boolean,
  quoteEmailContent: "",
  orderAcknowledgementEmailContent: "",
  purchaseOrderEmailContent: "",
  displayUnallocatedWarningBanner: false as boolean,
  displayAverageMargins: false as boolean,
  contributeToAverageMargins: false as boolean,
  sendPriceListReminders: false as boolean,
  isFortusSubBranch: false as boolean, // these are calculated attributes // NOT REAL SETTINGS
  isFortusBranch: false as boolean, // these are calculated attributes // NOT REAL SETTINGS
  useQuoteOrderStandardPricing: false as boolean,
  consolidateOrderRequestsMinute: 0 as number,
  allowOrderRequestConsolidation: false as boolean,
  defaultIntercoWarehouse: null as { id: string; name: string } | null,
  defaultDeliveryProductLocation: null as { id: string; name: string } | null,
  directDeliveryWarehouse: null as { id: string; name: string } | null,
  returnsWarehouse: null as { id: string; name: string } | null,
  goodsInTransitWarehouse: null as { id: string; name: string } | null,
  returnsInTransitWarehouse: null as { id: string; name: string } | null,
  allowInternalOrderTransfers: false as boolean,
  productImageFallbackFileboyId: "" as string,
  autoExportOrderRequests: false as boolean,
  viewAllBranchTasksInTheAllTasksView: "" as string,
  allowDisableAutoOrderRequestEmail: false as boolean,
  defaultAutoOrderRequestEmailDisabled: false as boolean,
  allowChangingQuoteOrderCurrency: false as boolean,
  canExportCustomers: false as boolean,
  quoteCreateProjectThreshold: 10000 as number,
  automaticallyGenerateStockTakes: false as boolean,
  canViewPoShortfallItemsPanel: false as boolean,
  canUseOpportunityCertificate: false as boolean,
  useQuoteSpecialItems: false as boolean,
  defaultPartShip: false as boolean,
  canRequestDirectDeliveryOR: false as boolean,
  canBulkAssignWarehouse: false as boolean,
  eoriNo: "" as string,
  hrRegion: "" as string,
  hideProductInfoStandardPricing: false as boolean,
  alwaysStockCheckProducts: [] as { id: string; stock_code: string; body: string }[],
  alertPctDiff: false as boolean,
  displayKits: false as boolean,
  branchImageId: "" as string,

  richQuoteSalesPhone: "" as string,
  richQuoteTechnicalPhone: "" as string,
  richQuoteEmail: "" as string,
  richQuoteWebsite: "" as string,
  richQuoteAddress: "" as string,
  autoEmailCashSalesInvoice: false as boolean,
  useOrderPaymentMethod: false as boolean,
  invoicesOnlyShowDiscountTotal: "" as string,
  customerReferencePrefixes: [] as { key: string; value: string }[] | undefined,
  useCustomerReferencePrefix: false as boolean,
  canSetOrderWarehouse: false as boolean | undefined,
  orderRequiredDateMandatory: false as boolean,
  useAddressLengthValidation: false as boolean | undefined,
  stockTakesUseMrge: false as boolean | undefined,
  mrgeBaseUrl: "" as string,
  showSagePurchaseOrdersOnGoodsReceiptsPage: false,
  defaultOrderRequestBranch: undefined as { id: string } | undefined,
  costCentre: "" as string,
  writeOffCategory: "" as string,
  writeOffCategories: [] as IWriteOffCategorySetting[] | undefined,
  rmaNotePresets: [
    "Please note no final crediting decision is made until the item(s) are inspected by RMA team.",
    "Please make sure all included RMA NUMBERS TO BE WRITTEN ON THE OUTER BOX, this makes sure receiving warehouse can book all RMA in.",
  ].map(x => ({ value: x, key: Math.random().toString(36) })) as { key: string; value: string }[],
  disableBulkExporting: false as boolean,
  autoDespatchOrdersInSage: false as boolean,
  _loaded: false,
}

let settingsCache: {
  promise: Promise<IBranchSettings>
  loadedAt: Date
} | null = null

const SETTINGS_RETRY_MIN_MS = 10 * 1000
const SETTINGS_RETRY_MAX_MS = 5 * 60 * 1000

export function useSettings() {
  const { currentUser } = useGlobalState()
  const [branchSettings, setBranchSettings] = useState<IBranchSettings>(defaultSettings)

  useInterval(loadSettingsViaCache, pollingIntervals.settingsRefetch)

  const retryTimeoutRef = useRef<number | undefined>(undefined)
  const retryDelayRef = useRef(SETTINGS_RETRY_MIN_MS)
  const visibilityListenerRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    loadSettingsViaCache()
  }, [currentUser.branch.id])

  useEffect(() => {
    return () => {
      window.clearTimeout(retryTimeoutRef.current)
      removeVisibilityListener()
    }
  }, [])

  function removeVisibilityListener() {
    if (visibilityListenerRef.current) {
      document.removeEventListener("visibilitychange", visibilityListenerRef.current)
      visibilityListenerRef.current = null
    }
  }

  /** Retry with backoff, waiting for the tab to be visible so hidden tabs don't keep polling */
  function scheduleRetry() {
    window.clearTimeout(retryTimeoutRef.current)
    const delay = retryDelayRef.current
    retryDelayRef.current = Math.min(delay * 2, SETTINGS_RETRY_MAX_MS)
    retryTimeoutRef.current = window.setTimeout(() => {
      if (!document.hidden) {
        loadSettingsViaCache()
        return
      }
      removeVisibilityListener()
      const listener = () => {
        if (!document.hidden) {
          removeVisibilityListener()
          loadSettingsViaCache()
        }
      }
      visibilityListenerRef.current = listener
      document.addEventListener("visibilitychange", listener)
    }, delay)
  }

  async function loadSettingsViaCache() {
    const cacheValid =
      settingsCache &&
      differenceInSeconds(new Date(), settingsCache.loadedAt) < 600

    let promise: Promise<IBranchSettings>

    if (cacheValid) {
      promise = settingsCache!.promise
    } else {
      const loadedAt = new Date()
      promise = loadSettingsForUser()

      settingsCache = {
        promise,
        loadedAt,
      }
    }

    let settings: IBranchSettings
    try {
      settings = await promise
    } catch (e) {
      console.error("Failed to load settings", e)
      // Don't keep serving the failed request from the cache; retry shortly
      if (settingsCache?.promise === promise) {
        settingsCache = null
      }
      scheduleRetry()
      return
    }

    retryDelayRef.current = SETTINGS_RETRY_MIN_MS

    setBranchSettings({
      ...settings,
      _loaded: true,
    })
  }

  return branchSettings
}

export async function loadSettingsForUser(userId?: string) {
  const user = await window.skeem.fetch2<{
    id: string
    settings: IBranchSettings
    branchSettings: IBranchSettings
    is_fortus_ew_branch: boolean
    isFortusBranch: boolean
    branch_id: string
  }>({
    first: {
      name: "skeem_users",
      attributes: [
        "settings",
        { as: "branch_id", value: { path: ["branch", "id"] } },
        { as: "branchSettings", value: { path: ["branch", "settings"] } },
        { as: "is_fortus_ew_branch", value: { path: ["branch", "is_fortus_ew_branch"] } },
        {
          as: "isFortusBranch",
          value: {
            exists: {
              path: [{ name: "branch", filter: { matches: { id: fortusEwBranchId } } }],
            },
          },
        },
      ],
      filter: { matches: { id: userId ?? { session: true } } },
    },
  })
  assert(user, "User was not returned")
  const merged = user?.branchSettings ?? {}

  return {
    ...defaultSettings,
    ...merged,
    // The default is only for before settings load; branches that never set it are off
    useExplicitAllocations: !!merged.useExplicitAllocations,
    isFortusSubBranch: user.is_fortus_ew_branch && !user.isFortusBranch,
    isFortusBranch: user.isFortusBranch,
  }
}
